import { ReactNode } from "react";
import { Box, Button, Chip, Skeleton, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { RevOpsBrief } from "Models/RevOpsBrief";
import { advisor } from "Components/AdvisorTheme";
import { formatCreatedAt, isStaleBrief, meaningfulSummary } from "Components/TodaysBrief";

interface PulseBannerProps {
  brief: RevOpsBrief | null | undefined;
  loading: boolean;
  error: boolean;
  profileError: boolean;
  onRetry: () => void;
  onRetryProfile: () => void;
}

/**
 * Short Revenue Pulse. Uses the brief's own summary when it is meaningful;
 * otherwise neutral introductory copy. Never derives an assessment from the
 * narrative sections.
 */
export default function PulseBanner({ brief, loading, error, profileError, onRetry, onRetryProfile }: PulseBannerProps) {
  let title: string;
  let body: ReactNode;
  if (profileError) {
    title = "Revenue Pulse";
    body = <span role="alert">Profile unavailable, so your brief can’t load. <Button size="small" onClick={onRetryProfile}>Retry profile</Button></span>;
  } else if (loading) {
    title = "Revenue Pulse";
    body = <><Skeleton width="90%" /><Skeleton width="60%" /><Box component="span" role="status" sx={{ position: "absolute", left: -9999 }}>Loading revenue brief…</Box></>;
  } else if (error) {
    title = "Revenue Pulse";
    body = <span role="alert">Brief unavailable. Access or subscription restrictions may apply. <Button size="small" onClick={onRetry}>Retry brief</Button></span>;
  } else if (!brief) {
    title = "Revenue Pulse";
    body = "No revenue brief available yet. Your daily brief will appear here once it has been generated.";
  } else {
    const summary = meaningfulSummary(brief.summary);
    title = summary ? "Revenue Pulse" : "Your daily revenue brief is ready";
    body = summary ?? "Review the priorities below, or open the full brief for what changed, risks, opportunities and recommendations.";
  }

  const stale = brief ? isStaleBrief(brief) : false;
  return (
    <Box
      component="section"
      aria-label="Revenue Pulse"
      sx={{
        display: "flex",
        gap: { xs: 1.5, sm: 2 },
        alignItems: "flex-start",
        bgcolor: advisor.lavender,
        border: `1px solid ${advisor.lavenderBorder}`,
        borderRadius: advisor.radius,
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 2.25 },
      }}
    >
      <Box aria-hidden sx={{ flexShrink: 0, width: 40, height: 40, borderRadius: "50%", bgcolor: "#FFFFFF", display: "grid", placeItems: "center", boxShadow: "0 1px 3px rgba(91,63,214,0.15)" }}>
        <AutoAwesomeIcon sx={{ color: advisor.purple, fontSize: 22 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
          <Typography component="h2" sx={{ fontSize: 17, fontWeight: 700, color: advisor.text, lineHeight: 1.3 }}>
            {title}
          </Typography>
          {brief && (
            <Chip
              size="small"
              label={stale ? `Brief from ${brief.date} · may be stale` : "Today’s brief"}
              title={`Brief date ${brief.date} · created ${formatCreatedAt(brief.created_at)}`}
              sx={{
                height: 22,
                fontSize: 12,
                fontWeight: 600,
                bgcolor: stale ? "#FFF4E0" : "#FFFFFF",
                color: stale ? "#8A5300" : advisor.muted,
                border: `1px solid ${stale ? "#F5D9A6" : advisor.lavenderBorder}`,
              }}
            />
          )}
        </Box>
        <Typography component="div" sx={{ fontSize: 15, color: advisor.muted, lineHeight: 1.55, overflowWrap: "anywhere" }}>
          {body}
        </Typography>
      </Box>
    </Box>
  );
}
